import { useNavigate, useParams } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import Sidebar from 'components/Sidebar'
import NetworkCard from 'components/NetworkCard'
import useRelations from 'hooks/api/useRelations'
import useUsers from 'hooks/api/useUsers'
import { getImageProcessorUrl, prepareMessageData, t } from 'helpers'
import { useCastingStore } from 'store'
import { CASTING_STATUS, MESSAGE_TYPE, RELATIONS } from 'types/enums'
import useContent from 'hooks/api/useContent'
import { getUser } from 'helpers/storage'
import { addRelation } from 'api/relations'
import useUser from 'hooks/api/useUser'
import { getArrayDates } from 'helpers/propertiesParser'

const CloseCastingSidebar = (): JSX.Element => {
	const [openSidebar, setOpenSidebar] = useCastingStore(
		useShallow((state) => [
			state.openCloseCastingSidebar,
			state.setOpenCloseCastingSidebar,
		]),
	)
	const navigate = useNavigate()
	const { roleId } = useParams()
	const { content, isLoading } = useContent(roleId)
	const { data, refresh } = useRelations(RELATIONS.ACCESS, roleId || '')
	const { data: playlists } = useRelations(
		RELATIONS.PLAYLIST,
		openSidebar && content ? content?.ContentId : '',
		'Content',
		'/Lookup',
	)

	const { content: production } = useContent(playlists?.[0]?.sourceId)

	const winnerStatus = data?.find(
		(rel) => rel.status === CASTING_STATUS.SELECTED,
	)

	const favoriteStatuses = data
		?.filter((rel) => rel.status === CASTING_STATUS.FAVORITE)
		.map((rel) => rel.targetId)

	const { user: winner } = useUser(winnerStatus?.targetId)
	const { users: favorites } = useUsers(favoriteStatuses)
	const { users: all } = useUsers(data?.map((rel) => rel.targetId))

	const selectWinner = async (
		userId: string,
		status: CASTING_STATUS,
	): Promise<void> => {
		await addRelation({
			sourceId: roleId,
			targetId: userId,
			type: 'Content',
			relation: RELATIONS.ACCESS,
			status: status,
		})
	}

	const sendMessage = async (): Promise<void> => {
		if (!winner || !roleId) return

		const { date: rehearsalDate } = getArrayDates(
			production?.ContentEventRehearsalDate,
		)
		const { date: filmingDate } = getArrayDates(
			production?.ContentEventFilmingDate,
		)

		const data = {
			userIds: [winner?.ContentId || ''],
			sourceId: roleId || '',
			key: 'NOTIFYWINNER',
			messageValue: encodeURIComponent(
				t('APP_NOTIFYWINNER_MESSAGE_BODY')
					?.replace('[RecipientName]', winner?.ContentTitle || '')
					?.replace('[YourName]', getUser()?.['Custom:User:Name'] || '')
					?.replace(
						'[AuditionTitle]',
						content?.['Globalizationen-USContentTitle'] || '',
					)
					?.replace('[RehearsalDate]', rehearsalDate)
					?.replace('[FilmingDate]', filmingDate),
			),
			redirect: window.location.pathname + window.location.hash,
			controlsProps: {
				prevText: 'APP_BACK',
				nextText: 'APP_SEND_NOTIF',
			},
			messageType: MESSAGE_TYPE.NOTIFY_WINNER,
		}
		navigate(prepareMessageData(data), {
			replace: true,
		})
		setOpenSidebar(false)
	}

	return (
		<Sidebar
			open={openSidebar}
			onClose={() => setOpenSidebar(false)}
			header={{
				title: 'APP_CLOSE_CASTING_SIDEBAR_TITLE',
				description: 'APP_CLOSE_CASTING_SIDEBAR_DESCRIPTION',
			}}
			loading={isLoading}
			buttons={[
				{
					children: t('APP_NOTIFY_WINNER_BUTTON'),
					onClick: sendMessage,
					disabled: !winner,
				},
			]}>
			<>
				<span>{t('APP_ALL_CANDIDATES')}</span>
				{all?.length ? (
					<div
						className="hideScrollBar"
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '16px',
							paddingTop: '5px',
							overflow: 'auto',
						}}>
						{all?.map((user) => (
							<div
								key={user?.ContentId}
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									gap: '5px',
								}}>
								<div
									style={{
										borderRadius: '50%',
										overflow: 'hidden',
										width: '50px',
										height: '50px',
										border: `2px solid ${
											user?.CustomerRole
												? `var(--${user.CustomerRole.toLowerCase()})`
												: 'transparent'
										}`,
									}}>
									<img
										src={
											getImageProcessorUrl(user?.CustomerImageSquareUrl, 50) ||
											'/placeholder.png'
										}
										alt=""
										style={{
											width: '100%',
											height: '100%',
											objectFit: 'cover',
										}}
									/>
								</div>
								<span
									className="caption-s textClamp"
									style={{
										textAlign: 'center',
										WebkitLineClamp: '2',
									}}
									dangerouslySetInnerHTML={{
										__html: user?.ContentTitle?.replaceAll(' ', '<br/>') || '',
									}}></span>
							</div>
						))}
					</div>
				) : (
					<span>{t('APP_RESULT_NOT_FOUND')}</span>
				)}
				<span>{t('APP_FAVORITE_CANDIDATES')}</span>
				{favorites?.length ? (
					<div
						className="hideScrollBar"
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '16px',
							paddingTop: '5px',
							overflow: 'auto',
						}}>
						{favorites?.map((user) => (
							<div
								key={user?.ContentId}
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									gap: '5px',
									cursor: 'pointer',
								}}
								onClick={async () => {
									if (winner) {
										await selectWinner(
											winner?.ContentId,
											CASTING_STATUS.FAVORITE,
										)
									}
									await selectWinner(user?.ContentId, CASTING_STATUS.SELECTED)
									await refresh()
								}}>
								<div
									style={{
										borderRadius: '50%',
										overflow: 'hidden',
										width: '50px',
										height: '50px',
										border: `2px solid ${
											user?.CustomerRole
												? `var(--${user.CustomerRole.toLowerCase()})`
												: 'transparent'
										}`,
									}}>
									<img
										src={
											getImageProcessorUrl(user?.CustomerImageSquareUrl, 50) ||
											'/placeholder.png'
										}
										alt=""
										style={{
											width: '100%',
											height: '100%',
											objectFit: 'cover',
										}}
									/>
								</div>
								<span
									className="caption-s textClamp"
									style={{
										textAlign: 'center',
										WebkitLineClamp: '2',
									}}
									dangerouslySetInnerHTML={{
										__html: user?.ContentTitle?.replaceAll(' ', '<br/>') || '',
									}}></span>
							</div>
						))}
					</div>
				) : (
					<span>{t('APP_RESULT_NOT_FOUND')}</span>
				)}

				{winner && (
					<>
						<span>{t('APP_CASTING_WINNER')}</span>
						<div className="grid2column">
							<NetworkCard
								data={winner}
								style={{
									border: `2px solid ${'var(--theme-primary)'}`,
								}}
							/>
						</div>
					</>
				)}
			</>
		</Sidebar>
	)
}

export default CloseCastingSidebar
